import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Mail,
  MessageCircle,
  Send,
  CheckCircle2,
  Users,
} from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import {
  CONTACT_EMAIL,
  CONTACT_TOPICS,
  HELP_FAQ,
  COMMUNITY_LINKS,
} from "../data/helpCenterContent";
import { submitContactEnquiry } from "../lib/api";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const inputClass =
  "w-full rounded-2xl px-4 py-3 text-sm outline-none transition-shadow focus:ring-2";

const inputStyle = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  color: "var(--text)",
};

const HelpCenterPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: CONTACT_TOPICS[0],
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await submitContactEnquiry(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Could not send your message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="help-center-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              Help center
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Contact the{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                PagePeers
              </span>{" "}
              community
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              Questions, feedback, or a gentle nudge in the right direction — send
              us a note and we will get back to you. You can also reach readers
              directly in our circles.
            </p>
          </motion.div>

          <div className="mt-16 md:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-7">
              <FadeIn>
                <div
                  className="rounded-3xl p-8 md:p-10 cozy-card"
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ background: "var(--brand-tint)" }}
                    >
                      <MessageCircle
                        className="w-5 h-5"
                        style={{ color: "var(--brand)" }}
                      />
                    </div>
                    <div>
                      <h2
                        className="font-serif text-2xl font-medium"
                        style={{ color: "var(--text)" }}
                      >
                        Send us a message
                      </h2>
                      <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                        We read every note from the reading room.
                      </p>
                    </div>
                  </div>

                  <AnimatePresence mode="wait">
                    {submitted ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-10"
                        data-testid="contact-success"
                      >
                        <CheckCircle2
                          className="w-12 h-12 mx-auto mb-4"
                          style={{ color: "var(--brand)" }}
                        />
                        <h3
                          className="font-serif text-xl font-medium mb-2"
                          style={{ color: "var(--text)" }}
                        >
                          Thank you for reaching out
                        </h3>
                        <p
                          className="text-sm max-w-sm mx-auto mb-6"
                          style={{ color: "var(--text-soft)" }}
                        >
                          We received your message and will reply to{" "}
                          <span style={{ color: "var(--text)" }}>
                            {form.email}
                          </span>{" "}
                          soon. You can also reach us at{" "}
                          <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="underline"
                            style={{ color: "var(--brand)" }}
                          >
                            {CONTACT_EMAIL}
                          </a>
                          .
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSubmitted(false);
                            setError("");
                            setForm({
                              name: "",
                              email: "",
                              topic: CONTACT_TOPICS[0],
                              message: "",
                            });
                          }}
                          className="text-sm px-5 py-2.5 rounded-full border"
                          style={{
                            borderColor: "var(--border)",
                            color: "var(--text)",
                          }}
                        >
                          Send another message
                        </button>
                      </motion.div>
                    ) : (
                      <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        className="space-y-5"
                        data-testid="contact-form"
                      >
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label
                              htmlFor="contact-name"
                              className="block text-xs font-medium mb-2 uppercase tracking-wider"
                              style={{ color: "var(--text-muted)" }}
                            >
                              Name
                            </label>
                            <input
                              id="contact-name"
                              name="name"
                              type="text"
                              required
                              value={form.name}
                              onChange={handleChange}
                              className={inputClass}
                              style={inputStyle}
                              placeholder="Your name"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="contact-email"
                              className="block text-xs font-medium mb-2 uppercase tracking-wider"
                              style={{ color: "var(--text-muted)" }}
                            >
                              Email
                            </label>
                            <input
                              id="contact-email"
                              name="email"
                              type="email"
                              required
                              value={form.email}
                              onChange={handleChange}
                              className={inputClass}
                              style={inputStyle}
                              placeholder="you@example.com"
                            />
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="contact-topic"
                            className="block text-xs font-medium mb-2 uppercase tracking-wider"
                            style={{ color: "var(--text-muted)" }}
                          >
                            Topic
                          </label>
                          <select
                            id="contact-topic"
                            name="topic"
                            value={form.topic}
                            onChange={handleChange}
                            className={inputClass}
                            style={inputStyle}
                          >
                            {CONTACT_TOPICS.map((topic) => (
                              <option key={topic} value={topic}>
                                {topic}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            htmlFor="contact-message"
                            className="block text-xs font-medium mb-2 uppercase tracking-wider"
                            style={{ color: "var(--text-muted)" }}
                          >
                            Message
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            rows={6}
                            value={form.message}
                            onChange={handleChange}
                            className={`${inputClass} resize-y min-h-[140px]`}
                            style={inputStyle}
                            placeholder="Tell us how we can help — shelf questions, circles, feedback, or just say hello."
                          />
                        </div>

                        {error ? (
                          <div
                            className="text-sm px-4 py-3 rounded-2xl"
                            style={{
                              background: "var(--brand-tint)",
                              color: "var(--brand)",
                              border: "1px solid var(--border)",
                            }}
                          >
                            {error}
                          </div>
                        ) : null}

                        <button
                          type="submit"
                          data-testid="contact-submit-btn"
                          disabled={loading}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                          style={{
                            background: "var(--brand)",
                            color: "var(--on-brand)",
                            boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                          }}
                        >
                          {loading ? "Sending..." : "Send message"}
                          <Send className="w-4 h-4" />
                        </button>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <FadeIn delay={0.08}>
                <div
                  className="rounded-3xl p-8"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <Mail
                    className="w-6 h-6 mb-4"
                    style={{ color: "var(--brand)" }}
                  />
                  <h2
                    className="font-serif text-xl font-medium mb-2"
                    style={{ color: "var(--text)" }}
                  >
                    Email us directly
                  </h2>
                  <p
                    className="text-sm mb-4 leading-relaxed"
                    style={{ color: "var(--text-soft)" }}
                  >
                    Prefer your own mail app? We are at the address below.
                  </p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-sm font-medium break-all"
                    style={{ color: "var(--brand)" }}
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </FadeIn>

              <FadeIn delay={0.12}>
                <div
                  className="rounded-3xl p-8 cozy-card"
                  style={{ background: "var(--surface)" }}
                >
                  <Users
                    className="w-6 h-6 mb-4"
                    style={{ color: "var(--brand)" }}
                  />
                  <h2
                    className="font-serif text-xl font-medium mb-4"
                    style={{ color: "var(--text)" }}
                  >
                    Reach the community
                  </h2>
                  <ul className="space-y-4">
                    {COMMUNITY_LINKS.map((link) => (
                      <li key={link.title}>
                        <p
                          className="font-medium text-sm mb-1"
                          style={{ color: "var(--text)" }}
                        >
                          {link.title}
                        </p>
                        <p
                          className="text-sm mb-2 leading-relaxed"
                          style={{ color: "var(--text-soft)" }}
                        >
                          {link.desc}
                        </p>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium"
                            style={{ color: "var(--brand)" }}
                          >
                            {link.cta}
                            <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <Link
                            to={link.to}
                            className="inline-flex items-center gap-1.5 text-sm font-medium"
                            style={{ color: "var(--brand)" }}
                          >
                            {link.cta}
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            </div>
          </div>

          <FadeIn className="mt-20 md:mt-28">
            <h2
              className="font-serif text-3xl font-medium text-center mb-10"
              style={{ color: "var(--text)" }}
            >
              Common questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {HELP_FAQ.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={item.q}
                    className="rounded-2xl overflow-hidden"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                      aria-expanded={isOpen}
                    >
                      <span
                        className="font-serif text-base md:text-lg font-medium"
                        style={{ color: "var(--text)" }}
                      >
                        {item.q}
                      </span>
                      <span
                        className="text-xl leading-none shrink-0"
                        style={{ color: "var(--brand)" }}
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease }}
                          className="overflow-hidden"
                        >
                          <p
                            className="px-6 pb-5 text-sm leading-relaxed"
                            style={{ color: "var(--text-soft)" }}
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </FadeIn>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default HelpCenterPage;
